<script setup lang="ts">
/**
 * Строка ⌘K (макеты 8-6, 8-7).
 *
 * Одна строка на всё: и быстрый переход, и вопрос ассистенту. Второе сочетание
 * под ассистента заводить некуда — ⌘K уже держит палитру, а разводить два
 * похожих окна по разным клавишам значит заставлять человека помнить, в каком
 * из них живёт нужное.
 *
 * Пустая строка показывает готовые команды, набранная — вопрос первой строкой и
 * переходы под ним. Ответ приезжает сюда же (макет 8-7): открывать панель ради
 * одного вопроса не нужно, а если разговор пошёл — «Развернуть в панель».
 *
 * Разговор и соединение живут в сторе ассистента, том же, что и у панели.
 * Поэтому развёрнутая панель показывает ровно то, что было в строке, а не
 * пустой экран.
 */
import {
  Book,
  ChevronRight,
  Clock,
  CornerDownLeft,
  FolderOpen,
  LayoutGrid,
  Plus,
  Search,
  Sparkle,
} from 'lucide-vue-next'
import { computed, nextTick, onMounted, onUnmounted, ref, watch, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { PALETTE_EVENT } from '@/components/palette'
import AssistantMarkdown from '@/features/assistant/AssistantMarkdown.vue'
import {
  PALETTE_GROUPS,
  paletteCommands,
  type PaletteCommand,
  type PaletteIcon,
} from '@/features/assistant/commands'
import DraftActionBlock from '@/features/assistant/DraftActionBlock.vue'
import { limitNotice } from '@/features/assistant/quota'
import TaskEventRow from '@/features/assistant/TaskEventRow.vue'
import { useAuthStore } from '@/features/auth/store'
import { searchPages, type PageHit } from '@/features/knowledge/api'
import { useAssistantStore } from '@/stores/assistant'

/** Пауза перед запросом: запрос на каждую букву кладёт FTS зря. */
const SEARCH_DELAY = 250
const ISSUE_KEY = /^[A-Za-z]+-\d+$/

const ICONS: Record<PaletteIcon, Component> = {
  search: Search,
  book: Book,
  plus: Plus,
  folder: FolderOpen,
  clock: Clock,
  board: LayoutGrid,
}

interface Row {
  kind: 'ask' | 'command' | 'issue' | 'page'
  icon: Component
  label: string
  meta: string
  snippet?: string
  command?: PaletteCommand
  target?: string
}

interface Section {
  caption: string
  rows: Row[]
}

const { t, locale } = useI18n()
const router = useRouter()
const auth = useAuthStore()
const assistant = useAssistantStore()

const isOpen = ref(false)
const query = ref('')
const hits = ref<PageHit[]>([])
const active = ref(0)
const field = ref<HTMLInputElement | null>(null)
const box = ref<HTMLElement | null>(null)

/** Вопрос, ответ на который показан в строке. Пусто — показан список. */
const asked = ref('')

let timer: ReturnType<typeof setTimeout> | undefined
let returnTo: HTMLElement | null = null

/** Без права ассистента строка остаётся палитрой перехода (макет 8-5b). */
const hasAssistant = computed(() => auth.can('tracker:ai'))

/**
 * Спрашивать можно (макет 8-5).
 *
 * Исчерпанный лимит и молчащий сервис убирают из строки ассистента, а не
 * дизейблят его строки: строка, на которую нельзя нажать, всё равно занимает
 * место и притворяется работающей. Переход при этом остаётся — он к ассистенту
 * отношения не имеет.
 */
const canAsk = computed(
  () => hasAssistant.value && !assistant.isBlocked && !assistant.isUnavailable,
)

/** Чем строка объясняет, почему ассистента в ней сейчас нет. */
const hint = computed(() => {
  if (hasAssistant.value && assistant.isBlocked) return limitNotice(assistant.quota, locale.value)
  if (hasAssistant.value && assistant.isUnavailable) return t('assistant.unavailable.title')

  return query.value.trim().length >= 2 ? t('palette.nothing') : t('palette.hint')
})

const issueKey = computed(() => {
  const needle = query.value.trim()

  return ISSUE_KEY.test(needle) ? needle.toUpperCase() : ''
})

const isAnswering = computed(() => Boolean(asked.value))

const commandRow = (command: PaletteCommand): Row => ({
  kind: 'command',
  icon: ICONS[command.icon],
  label: command.label,
  meta: command.meta,
  command,
})

/**
 * Что показывать списком.
 *
 * Пустая строка — готовые команды по группам (8-6). Набранная — вопрос первой
 * строкой: спрашивают чаще, чем прыгают, и Enter без выбора должен уходить
 * ассистенту, как в 8-7. Переходы остаются под ним: палитра жила на ⌘K до
 * ассистента, и терять её из-за него нельзя.
 */
const sections = computed<Section[]>(() => {
  const needle = query.value.trim()

  if (!needle) {
    if (!canAsk.value) return []

    const commands = paletteCommands(assistant.page)

    return PALETTE_GROUPS.map((group) => ({
      caption: t(`palette.groups.${group}`),
      rows: commands.filter((command) => command.group === group).map(commandRow),
    })).filter((section) => section.rows.length)
  }

  const ask: Section[] = canAsk.value
    ? [
        {
          caption: t('palette.groups.ask'),
          rows: [{ kind: 'ask', icon: Sparkle, label: needle, meta: t('palette.meta.assistant') }],
        },
      ]
    : []

  const jump: Row[] = []

  if (issueKey.value) {
    jump.push({
      kind: 'issue',
      icon: CornerDownLeft,
      label: t('palette.openIssue', { key: issueKey.value }),
      meta: '',
      target: `/issue/${issueKey.value}`,
    })
  }

  for (const hit of hits.value) {
    jump.push({
      kind: 'page',
      icon: Book,
      label: hit.title,
      meta: hit.spaceKey ?? '',
      snippet: hit.snippet,
      target: `/kb/${hit.id}`,
    })
  }

  if (!jump.length) return ask

  const go = { caption: t('palette.groups.jump'), rows: jump }

  // Набранный ключ задачи — не вопрос, а адрес: «DEV-1421» и Enter открывали
  // задачу до ассистента, и отдавать это сочетание модели нельзя.
  return issueKey.value ? [go, ...ask] : [...ask, go]
})

const rows = computed<Row[]>(() => sections.value.flatMap((section) => section.rows))

/** Ответ на последний вопрос: всё, что пришло после реплики человека. */
const answer = computed(() => {
  for (let index = assistant.items.length - 1; index >= 0; index--) {
    const item = assistant.items[index]

    if (item?.kind === 'message' && item.role === 'user') return assistant.items.slice(index + 1)
  }

  return assistant.items
})

/**
 * Показывать ли «Развернуть в панель».
 *
 * Своего сочетания у панели нет, и пока строка спрашивает — вход в неё через
 * заданный вопрос. Но с исчерпанным лимитом спросить нельзя, а прочитать
 * прошлый разговор человек вправе: без ссылки панель в этом состоянии не
 * открыть вовсе. Пустая строка при работающем ассистенте остаётся как в 8-6.
 */
const canExpand = computed(
  () => hasAssistant.value && (assistant.items.length > 0 || !canAsk.value),
)

const open = async () => {
  returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null
  isOpen.value = true

  // Остаток спрашивается при открытии, а не после вопроса: увидеть «лимит
  // исчерпан» уже набрав вопрос — худший момент из возможных.
  if (hasAssistant.value) void assistant.ensureQuota()

  await nextTick()

  field.value?.focus()
}

const close = () => {
  isOpen.value = false
  query.value = ''
  hits.value = []
  active.value = 0
  asked.value = ''

  // Фокус возвращается туда, откуда строку позвали: иначе он уезжает в начало
  // страницы, и следующий Tab начинает обход заново.
  returnTo?.focus()
  returnTo = null
}

const run = async () => {
  const needle = query.value.trim()

  if (asked.value || needle.length < 2 || issueKey.value) {
    hits.value = []

    return
  }

  try {
    hits.value = await searchPages(needle)
  } catch {
    // Поиск — вспомогательный путь: если сервис молчит, строка просто пуста.
    hits.value = []
  }
}

const ask = async (text: string) => {
  const question = text.trim()

  if (!question) return

  query.value = question
  asked.value = question

  await nextTick()

  // Вопрос остаётся в строке (8-7), но выделенным: следующий же символ
  // заменит его целиком, и «↵ следующий вопрос» не требует стирания руками.
  field.value?.select()

  await assistant.send(question)
}

/** Команду, которой не хватает текста человека, дописываем в строку. */
const fill = (command: PaletteCommand) => {
  query.value = command.prompt
  field.value?.focus()
}

const expand = () => {
  assistant.open()
  close()
}

const go = async (row: Row) => {
  if (row.kind === 'ask') {
    await ask(query.value)

    return
  }

  if (row.kind === 'command' && row.command) {
    if (row.command.fills) fill(row.command)
    else await ask(row.command.prompt)

    return
  }

  if (!row.target) return

  await router.push(row.target)

  close()
}

const runActive = () => {
  const row = rows.value[active.value]

  // Ни одной строки нет, а текст набран — это вопрос: Enter уходит ассистенту.
  if (!row) {
    if (canAsk.value && query.value.trim()) void ask(query.value)

    return
  }

  void go(row)
}

/**
 * Tab не выпускает фокус из строки: за ней лежит целый экран со ссылками, и
 * ушедший туда фокус человек уже не найдёт — окно поверх остаётся открытым.
 */
const trap = (event: KeyboardEvent) => {
  const found = box.value?.querySelectorAll<HTMLElement>('input, button')

  if (!found?.length) return

  event.preventDefault()

  const list = Array.from(found).filter((element) => !element.hasAttribute('disabled'))
  const index = list.indexOf(document.activeElement as HTMLElement)
  const next = list[(index + (event.shiftKey ? -1 : 1) + list.length) % list.length]

  next?.focus()
}

const isTyping = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false

  if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return true

  // Не `isContentEditable`: в редакторе событие приходит от той ноды, где
  // стоит курсор, а редактируемый корень — её предок.
  return Boolean(target.closest('[contenteditable="true"]'))
}

const onKey = (event: KeyboardEvent) => {
  if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
    // В редакторе ⌘K — вставка ссылки Tiptap, в поле ввода — своё сочетание
    // браузера. Отбирать их у человека, который что-то печатает, нельзя.
    if (!isOpen.value && isTyping(event.target)) return

    event.preventDefault()

    if (isOpen.value) close()
    else void open()

    return
  }

  if (!isOpen.value) return

  if (event.key === 'Escape') return close()

  if (event.key === 'Tab') return trap(event)

  if (event.key === 'ArrowDown') {
    event.preventDefault()
    active.value = rows.value.length ? (active.value + 1) % rows.value.length : 0
  }

  if (event.key === 'ArrowUp') {
    event.preventDefault()
    active.value = rows.value.length
      ? (active.value - 1 + rows.value.length) % rows.value.length
      : 0
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    runActive()
  }
}

watch(query, () => {
  // Набранное поверх заданного вопроса возвращает список: ответ на экране
  // относится к прошлой строке, а не к тому, что печатают сейчас.
  if (query.value !== asked.value) asked.value = ''

  active.value = 0

  if (timer) clearTimeout(timer)

  timer = setTimeout(() => void run(), SEARCH_DELAY)
})

// На телефоне сочетания нет: строку открывает вкладка «Поиск».
const onExternal = () => void open()

onMounted(() => {
  window.addEventListener('keydown', onKey)
  window.addEventListener(PALETTE_EVENT, onExternal)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKey)
  window.removeEventListener(PALETTE_EVENT, onExternal)

  if (timer) clearTimeout(timer)
})
</script>

<template>
  <div
    v-if="isOpen"
    class="cpal"
    role="dialog"
    aria-modal="true"
    :aria-label="t('palette.title')"
    @click.self="close"
  >
    <div ref="box" class="cpal__box">
      <div class="cpal__input">
        <Sparkle class="cpal__spark" :size="16" />

        <input
          ref="field"
          v-model="query"
          class="cpal__field"
          :placeholder="t('palette.placeholder')"
          :aria-label="t('palette.title')"
        />

        <span class="cpal__esc">Esc</span>
      </div>

      <!-- Ответ на месте списка, а не под ним: строка не должна вырастать в
           панель — для длинного разговора есть сама панель. -->
      <div v-if="isAnswering" class="cpal__result" aria-live="polite" aria-atomic="false">
        <template v-for="item in answer" :key="item.id">
          <TaskEventRow
            v-if="item.kind === 'call'"
            :item="item"
            :can-retry="!assistant.isStreaming"
            @retry="assistant.retry()"
          />

          <DraftActionBlock
            v-else-if="item.kind === 'draft'"
            :item="item"
            :busy="assistant.isStreaming"
            @decide="assistant.decide(item, $event)"
          />

          <AssistantMarkdown v-else-if="item.role === 'assistant'" :text="item.text" />
        </template>

        <p v-if="assistant.isStreaming && !assistant.hasText" class="cpal__waiting">
          {{ t('assistant.waiting') }}
        </p>

        <p v-if="assistant.error" class="cpal__error" role="alert">{{ assistant.error }}</p>
      </div>

      <template v-else>
        <ul v-if="rows.length" class="cpal__list">
          <template v-for="section in sections" :key="section.caption">
            <li class="cpal__caption">{{ section.caption }}</li>

            <li v-for="row in section.rows" :key="row.label">
              <button
                type="button"
                class="cpal__row"
                :class="{ 'cpal__row--active': rows[active] === row }"
                @click="go(row)"
              >
                <component :is="row.icon" class="cpal__icon" :size="16" />

                <span class="cpal__text">
                  <span class="cpal__title">{{ row.label }}</span>
                  <span v-if="row.snippet" class="cpal__snippet">{{ row.snippet }}</span>
                </span>

                <span v-if="row.meta" class="cpal__meta">{{ row.meta }}</span>
              </button>
            </li>
          </template>
        </ul>

        <p v-else class="cpal__hint">{{ hint }}</p>
      </template>

      <div class="cpal__foot">
        <template v-if="isAnswering">
          <span class="cpal__key">{{ t('palette.footer.next') }}</span>
        </template>

        <template v-else>
          <span class="cpal__key">{{ t('palette.footer.select') }}</span>
          <span class="cpal__key">{{ t('palette.footer.run') }}</span>
        </template>

        <span class="cpal__spacer" />

        <button v-if="isAnswering || canExpand" type="button" class="cpal__expand" @click="expand">
          {{ t('palette.footer.expand') }}
          <ChevronRight :size="14" />
        </button>

        <span v-else-if="canAsk" class="cpal__key">{{ t('palette.footer.here') }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cpal {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 120px;
  background: rgb(0 0 0 / 18%);
}

.cpal__box {
  display: flex;
  flex-direction: column;
  width: 640px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 160px);
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg);
}

.cpal__input {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  height: 52px;
  padding: 0 14px 0 16px;
  border-bottom: 1px solid var(--color-border);
}

.cpal__spark {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.cpal__field {
  flex: 1;
  min-width: 0;
  border: none;
  background: none;
  color: var(--color-text-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
}

.cpal__field:focus {
  outline: none;
}

.cpal__esc {
  flex-shrink: 0;
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  background: var(--color-bg-muted);
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
}

.cpal__list {
  margin: 0;
  padding: 6px 0 8px;
  overflow-y: auto;
  list-style: none;
}

.cpal__caption {
  padding: 10px 16px 4px;
  color: var(--color-text-muted);
  font-size: var(--font-size-overline);
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
}

.cpal__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  padding: 0 16px;
  border: none;
  background: none;
  font-family: var(--font-family-sans);
  text-align: left;
  cursor: pointer;
}

.cpal__row:hover,
.cpal__row--active {
  background: var(--color-bg-muted);
}

.cpal__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.cpal__text {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 2px;
  min-width: 0;
  padding: 6px 0;
}

.cpal__title {
  overflow: hidden;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cpal__snippet {
  overflow: hidden;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.cpal__meta {
  flex-shrink: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.cpal__hint {
  margin: 0;
  padding: 16px;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.cpal__result {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px 14px;
  overflow-y: auto;
}

.cpal__waiting,
.cpal__error {
  margin: 0;
  font-size: var(--font-size-caption);
}

.cpal__waiting {
  color: var(--color-text-muted);
}

.cpal__error {
  color: var(--color-danger);
}

.cpal__foot {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
  height: 34px;
  padding: 0 16px;
  border-top: 1px solid var(--color-border);
}

.cpal__key {
  color: var(--color-text-muted);
  font-size: var(--font-size-caption);
}

.cpal__spacer {
  flex: 1;
}

.cpal__expand {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-primary);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}
</style>
